// useNewsForSignals — TanStack Query hook used by the hot-zone news card
// (W2-B). Given a set of signal ids and the active filter state, returns the
// news rows tied to those signals (oldest-first → newest-first sort).
//
// Wave 2 serves from the static `SAMPLE_NEWS` fixture; the Inference
// orchestrator swaps `queryFn` to POST `/v1/news/feed?by=signal` later. The
// fixture-vs-API switch is one import change because both share the
// `NewsItem` Zod contract.
//
// Iron Rule #11: lives in `intel-feed-client` so the Vendor + Agency maps
// reuse the same lookup model. Iron Rule #4 / #12: zero LLM in the request
// path; the credibility chip uses the deterministic stub.

import type { filters as f } from '@eidollon/contracts';
import { useQuery } from '@tanstack/react-query';
import { applyNewsFilter } from './filter.js';
import { getSampleNews } from './fixtures/sample-news.js';
import type { NewsItem } from './types.js';

export interface UseNewsForSignalsOptions {
  /** Signal ids that the card should look up news for. Empty → no fetch. */
  signalIds: readonly string[];
  /** Active Intel filter state; news rows respect the same severity/region/time bounds. */
  filterState: f.FilterState;
  /** Cap on returned rows. Default 12 — the "+N RELATED" chip fans out from this. */
  limit?: number;
}

export interface UseNewsForSignalsResult {
  rows: readonly NewsItem[];
  loading: boolean;
  error: string | null;
}

/**
 * Pure function — exposed for tests and for the hook implementation. Filters
 * `SAMPLE_NEWS` by `linked_signal_ids ⊇ signalIds` then by `filterState`,
 * sorts newest-first, caps at `limit`.
 */
export function selectNewsForSignals(
  rows: readonly NewsItem[],
  signalIds: readonly string[],
  state: f.FilterState,
  limit: number,
): NewsItem[] {
  const idSet = new Set(signalIds);
  const matched =
    idSet.size === 0 ? [] : rows.filter((r) => r.linked_signal_ids.some((id) => idSet.has(id)));
  const filtered = applyNewsFilter(matched, state);
  filtered.sort((a, b) => (a.published_at < b.published_at ? 1 : -1));
  return filtered.slice(0, limit);
}

export function useNewsForSignals(opts: UseNewsForSignalsOptions): UseNewsForSignalsResult {
  const { signalIds, filterState, limit = 12 } = opts;
  const q = useQuery<NewsItem[]>({
    queryKey: [
      'news-for-signals',
      signalIds.slice().sort().join(','),
      JSON.stringify(filterState),
      limit,
    ],
    queryFn: async () => {
      // TODO(inference orchestrator): swap to
      //   const res = await fetch(`${API_BASE_URL}/v1/news/feed`, {...});
      //   return NewsFeedResponse.parse(await res.json()).items;
      return selectNewsForSignals(getSampleNews(), signalIds, filterState, limit);
    },
    enabled: signalIds.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  return {
    rows: q.data ?? [],
    loading: q.isLoading,
    error: q.error instanceof Error ? q.error.message : null,
  };
}
